import {validateFieldLength} from '../../../../../utility/validationUtil';

// AD-2719 (Maria, 17 October 2026): adding or editing a contact asks one question at a time. These are the pages,
// the words on each and the checks before 'Next', kept apart from the screen so they can be tested.

// Which pages show. A parent editing their own contact changes only what they can change today: the title, email,
// phone number and home address.
export const contactSteps = ({mode, isParentOwnContact, sameAddress, peopleAtAddress = []}) => {
    // Maria, 18 October 2026: editing a contact uses the same pages as adding one; the home address page only
    // when the contact does not live with the student
    // AD-2721: when the school adds a contact at a separate address where other people at the school live, a page
    // lists them and says the addresses will be linked (Maria, 29 October 2026: no choice to keep them separate)
    const linkAddress = mode === 'add' && !sameAddress && peopleAtAddress.length > 0 ? ['linkAddress'] : [];
    const address = sameAddress ? [] : ['address', ...linkAddress];
    if (mode === 'edit' && isParentOwnContact) {
        return ['who', 'contact', ...address, 'check'];
    }

    // Maria, 18 October 2026: the phone number, email address and whether the contact lives with the student
    // second, then the home address if not
    return ['who', 'contact', ...address, 'responsibility', 'courtOrder', 'priority', 'check'];
};

export const emptyNewContact = () => ({
    titleId: null,
    forename: '',
    surname: '',
    relationType: '',
    parentalResponsibility: null,
    legalOrder: null,
    priority: '',
    email: '',
    phoneNumber: '',
    // Maria, 17 October 2026: the box has no Email Notifications choice. Every new contact has email
    // notifications on; a contact unsubscribes themselves after creating their account.
    emailNotificationsEnabled: true,
    sameAddress: null,
    address: null,
    // Maria, 29 October 2026: nobody's address is changed for the others at it without asking, so neither choice
    // under 'Address Update Options' is made until the person chooses one
    updateAllLinkedAddresses: null
});

const ADDRESS_FIELDS = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode'];

// An address as the address search uses it, from a contact's or a child's stored address.
export const toLookupAddress = stored => {
    if (!stored) return null;
    const address = Object.fromEntries(ADDRESS_FIELDS.map(field => [field, stored[field] ?? '']));
    address.countryCode = stored.countryCode || stored.country || '';

    return ADDRESS_FIELDS.some(field => address[field]) ? address : null;
};

// The contact being edited, ready for the pages.
// The same home address: the same postcode, house number or name, street and flat, ignoring spaces and capitals.
const addressKey = address => (address
    ? ['postcode', 'houseNumber', 'houseName', 'street', 'apartment']
        .map(field => String(address[field] ?? '').replace(/\s+/g, '').toLowerCase()).join('|')
    : '');
export const livesAtChildAddress = (address, childAddress) =>
    Boolean(address?.postcode && childAddress?.postcode) && addressKey(address) === addressKey(childAddress);

export const contactFromExisting = (existing, childAddress) => ({
    ...emptyNewContact(),
    titleId: existing.titleId ?? null,
    forename: existing.forename ?? '',
    surname: existing.surname ?? '',
    relationType: existing.relationType ?? '',
    parentalResponsibility: Boolean(existing.parentalResponsibility),
    legalOrder: Boolean(existing.legalOrder),
    priority: existing.priority == null ? '' : String(existing.priority),
    email: existing.email ?? '',
    phoneNumber: existing.phoneNumber ?? '',
    emailNotificationsEnabled: existing.emailNotificationsEnabled ?? true,
    address: toLookupAddress(existing.address),
    originalAddress: toLookupAddress(existing.address),
    // Maria, 18 October 2026: the answer to 'Does Helen live at the same address as Jemma?' starts from the
    // addresses already held
    sameAddress: livesAtChildAddress(existing.address, childAddress)
});

// The contact's forename once it is typed, otherwise "this contact".
export const contactName = contact => contact.forename?.trim() || 'this contact';

const possessive = name => `${name}'s`;

// Maria, 18 October 2026: a question above each box on the Contact Details page, in place of the words in the boxes
export const emailQuestion = contact => `Please enter ${possessive(contactName(contact))} email address`;
export const phoneQuestion = contact => `Please enter ${possessive(contactName(contact))} phone number`;

export const questionFor = (step, contact, studentName, mode = 'add') => {
    const name = contactName(contact);

    return {
        // Maria, 17 October 2026
        // Maria, 18 October 2026: 'Contact Details' when adding as well
        who: 'Contact Details',
        // Maria, 18 October 2026: the heading is 'Parental Responsibility'; the question is asked above the answers
        responsibility: 'Parental Responsibility',
        courtOrder: `Is there a restrictive court order relating to ${name}?`,
        priority: `What priority should ${name} be given?`,
        // Maria, 18 October 2026: the page also asks about the home address, when adding or editing
        contact: 'Contact Details and Home Address',
        // Maria, 18 October 2026: asked on the Contact Details page when a contact is added
        where: `Does ${name} live at the same address as ${studentName}?`,
        address: `What is ${possessive(name)} home address?`,
        check: `Please check ${possessive(name)} details.`
    }[step];
};

export const responsibilityQuestion = (contact, studentName) => `Does ${contactName(contact)} have Parental Responsibility for ${studentName}?`;

// Maria, 18 October 2026: the line under the answers once No is chosen to Parental Responsibility
export const responsibilityNoHelp = (contact, studentName) =>
    `${contactName(contact)} will not receive an Admicity Parent account invitation and will not have access to ${possessive(studentName)} information.`;

export const responsibilityYesLine = (contact, studentName) =>
    `${contactName(contact)} will be invited to create an Admicity Parent account and will have full access to ${possessive(studentName)} profile information.`;

// Maria's wording for the Court Order page, 17 October 2026.
export const COURT_ORDER_CHOICES = {
    no: 'I confirm there is no restrictive legal order in place',
    yes: 'There is a restrictive court order in place and I will provide details of this to the school office.'
};
export const courtOrderLine = studentName => `Select Yes if a legal order is currently in force with conditions relating to contact with or information about ${studentName}, such as a prohibited steps order, a specific issue order or a child arrangements order that limits contact.`;
// Maria, 18 October 2026: a line under the answers once 'There is a restrictive court order' is chosen, in place of
// the last sentence of the description
export const courtOrderYesLine = contact => `${contactName(contact)} will NOT be sent an automated invitation to create an Admicity Parent account.`;

export const priorityLine = contact => (contact.parentalResponsibility
    // Maria, 18 October 2026
    ? 'Enter Priority 1 or Priority 2.'
    : 'Enter Priority 3 or Priority 4.');

// Maria's wording for where the contact lives, 17 October 2026.
// Maria, 18 October 2026: Yes or No to 'Does Helen live at the same address as Jemma?'
export const whereChoices = studentName => ({
    same: 'Yes',
    separate: 'No',
    sameOnCheckPage: `Same address as ${studentName}`
});

// Today's choices from the house icon's 'Address Update Options', word for word.
export const ADDRESS_UPDATE_CHOICES = {
    current: ['Update address for this contact only', "Only this contact's address will be updated"],
    all: ['Update address for all family members', 'Update address for all family members who share the same current address']
};

// Whether the home address on the page is different from the one held, field by field, ignoring spaces and capitals.
const fullAddressKey = address => (address
    ? [...ADDRESS_FIELDS, 'countryCode'].map(field => String(address[field] ?? '').replace(/\s+/g, '').toLowerCase()).join('|')
    : '');
export const addressChanged = contact => fullAddressKey(contact.address) !== fullAddressKey(contact.originalAddress);

// The rule the house icon uses: a postcode and either a town or a street.
export const isAddressComplete = address => Boolean(address &&
    address.postcode?.toString().trim() &&
    (address.town?.toString().trim() || address.street?.toString().trim()));

export const ADDRESS_RULE = 'Address must have a postcode AND either a town or street';

export const formatAddress = address => (address
    ? [address.apartment, address.houseName, [address.houseNumber, address.street].filter(Boolean).join(' '), address.district, address.town, address.county, address.postcode]
        .map(part => part?.toString().trim()).filter(Boolean).join(', ')
    : '');

// 'Tom Jenkins', 'Tom Jenkins and Amy Jenkins', 'Tom Jenkins, Amy Jenkins and Sam Jenkins'
const joinNames = names => (names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

// What a person is told when an email address is changed in the edit box.
export const emailChangeLines = ({originalEmail, newEmail, isParentOwnContact, contact, studentName, otherChildren = []}) => {
    const before = originalEmail?.trim() ?? '';
    const after = newEmail?.trim() ?? '';
    if (!before || before.toUpperCase() === after.toUpperCase()) return [];

    if (isParentOwnContact) {
        const lines = [`If you change your email address, we will send an invitation to ${after || 'your new address'} to create a new Admicity Parent account. You will no longer be able to sign in with ${before} to see ${possessive(studentName)} profile.`];
        // AD-2723 (Maria): the same address on the parent's other children changes too
        if (otherChildren.length > 0) {
            lines.push(`Your email address will also be changed on ${possessive(joinNames(otherChildren))} ${otherChildren.length > 1 ? 'profiles' : 'profile'}, so you can continue to see all your children in your new Admicity account.`);
        }
        return lines;
    }

    return [`If you change this email address, ${contactName(contact)} will be sent an invitation to ${after || 'the new address'} to create a new Admicity Parent account, and will no longer be able to sign in with ${before} to see ${possessive(studentName)} profile.`];
};

export const DECLARATION = 'By choosing this answer you are making a formal declaration that this information is accurate.';

// Maria, 17 October 2026: the priority starts at 1 for a contact with Parental Responsibility and 3 for anyone else.
export const defaultPriority = parentalResponsibility => (parentalResponsibility ? '1' : '3');

// The same priority rules as the previous box.
export const priorityError = value => {
    if (!value || value.length === 0) return 'This field is required.';
    if (!/^-?[0-9]+$/.test(value)) return 'Only numbers are allowed.';
    if (parseInt(value, 10) <= 0) return 'The Priority cannot be less than 1';
    if (parseInt(value, 10) > 10) return 'The Priority cannot be more than 10';

    return '';
};

export const nameError = value => {
    const {isValid, message} = validateFieldLength(value?.trim() ?? '', 128);

    return isValid ? '' : message;
};

// Whether 'Next' can be used on a page. The email and phone page is checked with the profile's own rules on 'Next'.
export const canGoOn = (step, contact) => {
    switch (step) {
        case 'who':
            return !nameError(contact.forename) && !nameError(contact.surname) && Boolean(contact.relationType);
        case 'responsibility':
            return contact.parentalResponsibility !== null;
        case 'courtOrder':
            return contact.legalOrder !== null;
        case 'priority':
            return !priorityError(contact.priority);
        case 'contact':
            // Maria, 18 October 2026: the email address is required, and a new contact's page also asks whether
            // they live at the same address as the student
            return Boolean(contact.email.trim()) && contact.sameAddress !== null;
        case 'where':
            return contact.sameAddress !== null;
        case 'address':
            // Maria, 29 October 2026: a changed address is saved only after choosing who it is changed for
            return isAddressComplete(contact.address) &&
                (contact.originalAddress === undefined || !addressChanged(contact) || contact.updateAllLinkedAddresses !== null);
        default:
            return true;
    }
};

const sentAddress = address => ({...address, country: address.countryCode});

// What is sent on 'Save': the same fields as before, with the home address.
// AD-2721: 'Helen Jenkins, Mother of Jemma Jenkins', or 'Jemma Jenkins' for a child
export const personAtAddressLine = person => (person.description ? `${person.fullName}, ${person.description}` : person.fullName);

// Maria, 29 October 2026
export const linkAddressLine = forename => `${forename}'s address will be linked in SIMS to the address of the people listed above. If any of them changes their home address later, they will be asked whether to change it for everyone at the address or for themselves only, so the address can be unlinked at any time.`;

export const contactPayload = (contact, mode = 'add', childAddress = null, peopleAtAddress = []) => {
    const fields = {
        titleId: contact.titleId ?? null,
        forename: contact.forename.trim(),
        surname: contact.surname.trim(),
        email: contact.email.trim(),
        phoneNumber: contact.phoneNumber.trim(),
        relationType: contact.relationType,
        priority: contact.priority,
        legalOrder: Boolean(contact.legalOrder),
        parentalResponsibility: Boolean(contact.parentalResponsibility),
        emailNotificationsEnabled: contact.emailNotificationsEnabled
    };

    if (mode === 'edit') {
        // a contact who lives with the student is given the student's address, for this contact only
        return contact.sameAddress && childAddress
            ? {...fields, address: sentAddress(toLookupAddress(childAddress)), updateAllLinkedAddresses: false}
            : {...fields, address: contact.address ? sentAddress(contact.address) : null, updateAllLinkedAddresses: contact.updateAllLinkedAddresses === true};
    }

    return contact.sameAddress
        ? {...fields, isSameAddressAsChild: true, address: null}
        : {
            ...fields,
            isSameAddressAsChild: false,
            address: contact.address ? sentAddress(contact.address) : null,
            // AD-2721: everyone listed lives at the same address; the contact is linked to the first person's in SIMS
            linkAddressToSimsPersonId: peopleAtAddress.length > 0 ? peopleAtAddress[0].simsPersonId : null
        };
};

export const newContactPayload = contact => contactPayload(contact, 'add');
